import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { clients } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { PageLayout, SearchInput, Skeleton } from '../../components/ui'

function ClientCard({ client }) {
  const initials = `${client.firstName?.[0] || ''}${client.lastName?.[0] || ''}`.toUpperCase()
  const isInactive = client.isActive === false

  return (
    <Link
      to={`/clients/${client.id}`}
      className={`flex items-center gap-3 p-4 bg-dial-surface border border-dial-border rounded-xl active:bg-dial-elevated transition-colors ${isInactive ? 'opacity-50' : ''}`}
    >
      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold font-display ${
        isInactive ? 'bg-dial-elevated text-indices-muted' : 'bg-lume-teal/20 text-lume-teal'
      }`}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`font-medium truncate ${isInactive ? 'text-indices-muted' : 'text-indices-bright'}`}>
            {client.firstName} {client.lastName}
          </p>
          {isInactive && (
            <span className="px-1.5 py-0.5 text-[10px] rounded bg-dial-elevated text-indices-muted border border-dial-border">
              Inactive
            </span>
          )}
        </div>
        <p className="text-sm text-indices-muted truncate">{client.email}</p>
      </div>
      {client.isActive === false ? (
        <span className="px-2 py-1 text-xs rounded-full font-medium bg-dial-elevated text-indices-muted">
          archived
        </span>
      ) : (
        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
          client.trainingType === 'in_person' ? 'bg-status-success/20 text-status-success' :
          client.trainingType === 'remote' ? 'bg-status-info/20 text-status-info' :
          'bg-purple-500/20 text-purple-400'
        }`}>
          {client.trainingType?.replace('_', ' ')}
        </span>
      )}
    </Link>
  )
}

export default function ClientListPage() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('active')
  const [showInactive, setShowInactive] = useState(false)

  const { data: clientList, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: clients.list,
  })

  // Compute counts for each filter category
  const counts = useMemo(() => {
    if (!clientList) return { active: 0, archived: 0, all: 0 }
    const active = clientList.filter(c => c.isActive !== false).length
    const archived = clientList.filter(c => c.isActive === false).length
    return { active, archived, all: clientList.length }
  }, [clientList])

  const activeCount = counts.active
  const inactiveCount = counts.archived

  const filteredClients = useMemo(() => {
    if (!clientList) return []

    return clientList.filter(client => {
      // Apply active/archived filter
      if (filter === 'active' && client.isActive === false) return false
      if (filter === 'archived' && client.isActive !== false) return false

      // Apply search filter
      if (search) {
        const searchLower = search.toLowerCase()
        return (
          client.firstName?.toLowerCase().includes(searchLower) ||
          client.lastName?.toLowerCase().includes(searchLower) ||
          client.email?.toLowerCase().includes(searchLower)
        )
      }
      return true
    })
  }, [clientList, filter, search])

  const filterOptions = [
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'archived', label: 'Archived', count: counts.archived },
    { key: 'all', label: 'All', count: counts.all },
  ]

  return (
    <PageLayout>
      <PageHeader
        title="Clients"
        showBack
        backTo="/"
        actions={
          <Link
            to="/clients/new"
            className="p-2 text-lume-teal hover:text-lume-green transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </Link>
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Filter pills */}
        <div className="flex gap-2">
          {filterOptions.map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === key
                  ? 'bg-lume-teal text-dial-black'
                  : 'bg-dial-surface text-indices-medium border border-dial-border hover:bg-dial-elevated'
              }`}
            >
              {label}{!isLoading && ` (${count})`}
            </button>
          ))}
        </div>

        {/* Search */}
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search clients..."
        />

        {/* Active/Inactive toggle */}
        {inactiveCount > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-xs text-indices-muted">{activeCount} active</span>
            <button
              onClick={() => setShowInactive(!showInactive)}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                showInactive
                  ? 'bg-dial-elevated text-indices-bright border-dial-border'
                  : 'text-indices-muted border-transparent hover:text-indices-medium'
              }`}
            >
              {showInactive ? `Showing ${inactiveCount} inactive` : `+ ${inactiveCount} inactive`}
            </button>
          </div>
        )}

        {/* Client List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-20 rounded-xl" />
            ))}
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-indices-muted">
              {filter === 'archived'
                ? 'No archived clients'
                : search
                  ? 'No clients found'
                  : 'No clients yet'}
            </p>
            {filter === 'active' && !search && (
              <Link to="/clients/new" className="text-lume-teal font-medium mt-2 inline-block hover:text-lume-green transition-colors">
                Add your first client
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredClients.map(client => (
              <ClientCard key={client.id} client={client} />
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  )
}
